import { useEffect, useState, type FormEvent } from "react";
import { Eye, EyeOff, LockKeyhole, Plus, ShieldCheck } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { Button } from "../../ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { createGCPAdminManagedSecret } from "./Api/gcpserviceapi";

interface AddAdminSecretDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    tenantName: string;
    projectId: string;
    onCreated: () => Promise<void> | void;
}

const INITIAL_FORM = { name: "", secretValue: "" };
const SECRET_NAME_PATTERN = /^[A-Za-z0-9_-]+$/;

const getErrorMessage = (error: any) => {
    if (typeof error === "string") return error;
    return error?.message || error?.error || "Unable to create the secret. Please try again.";
};

export default function AddAdminSecretDialog({
    open,
    onOpenChange,
    tenantName,
    projectId,
    onCreated,
}: AddAdminSecretDialogProps) {
    const [form, setForm] = useState(INITIAL_FORM);
    const [showValue, setShowValue] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { toast } = useToast();

    useEffect(() => {
        if (!open) {
            setForm(INITIAL_FORM);
            setShowValue(false);
        }
    }, [open]);

    const trimmedName = form.name.trim();
    const nameError = trimmedName && !SECRET_NAME_PATTERN.test(trimmedName)
        ? "Use only letters, numbers, hyphens, and underscores."
        : "";
    const isSubmitDisabled = !trimmedName || !form.secretValue || !!nameError || isSubmitting || !tenantName || !projectId;

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (isSubmitDisabled) return;

        setIsSubmitting(true);
        try {
            await createGCPAdminManagedSecret({
                tenantName,
                projectId,
                name: trimmedName,
                secretValue: form.secretValue,
            });
            await onCreated();
            toast({
                title: "Admin-managed secret added",
                description: `“${trimmedName}” was saved to GCP Secret Manager and will be included in discovery scans.`,
            });
            onOpenChange(false);
        } catch (error: any) {
            toast({
                title: "Secret could not be added",
                description: getErrorMessage(error),
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(nextOpen) => !isSubmitting && onOpenChange(nextOpen)}>
            <DialogContent className="sm:max-w-md rounded-2xl border-border/70">
                <DialogHeader className="text-left">
                    <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <LockKeyhole className="h-5 w-5" />
                    </div>
                    <DialogTitle>Add admin-managed secret</DialogTitle>
                    <DialogDescription>
                        Create a discovery target in GCP Secret Manager. Its value will never be shown after saving.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-5 pt-2">
                    <div className="space-y-2">
                        <Label htmlFor="admin-secret-name">Secret name</Label>
                        <Input
                            id="admin-secret-name"
                            value={form.name}
                            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                            placeholder="e.g. payments-api-token"
                            autoComplete="off"
                            maxLength={255}
                            disabled={isSubmitting}
                            aria-invalid={!!nameError}
                            data-testid="input-admin-secret-name"
                        />
                        {nameError && <p className="text-xs text-destructive">{nameError}</p>}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="admin-secret-value">Secret value</Label>
                        <div className="relative">
                            <Input
                                id="admin-secret-value"
                                type={showValue ? "text" : "password"}
                                value={form.secretValue}
                                onChange={(event) => setForm((current) => ({ ...current, secretValue: event.target.value }))}
                                placeholder="Enter the value once"
                                autoComplete="new-password"
                                maxLength={65536}
                                disabled={isSubmitting}
                                className="pr-10"
                                data-testid="input-admin-secret-value"
                            />
                            <button
                                type="button"
                                onClick={() => setShowValue((current) => !current)}
                                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                                aria-label={showValue ? "Hide secret value" : "Show secret value"}
                                disabled={isSubmitting}
                            >
                                {showValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    <div className="flex gap-3 rounded-xl border border-primary/15 bg-primary/5 p-3 text-xs text-muted-foreground">
                        <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                        <p>
                            Saved with automatic replication. WhiteSwan stores an encrypted copy for matching during future scans; APIs and pages never return the value.
                        </p>
                    </div>

                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitDisabled} data-testid="button-save-admin-secret">
                            {isSubmitting ? (
                                <span className="flex items-center gap-2">
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/40 border-t-primary-foreground" />
                                    Saving…
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    <Plus className="h-4 w-4" /> Add secret
                                </span>
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
